import { useEffect, useRef, useState } from "react";
import Layout from "@/components/Layout";
import { api, API, fmtBRL, fmtDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Pencil, X, Paperclip, FileText, Loader2, BookOpen, Eye } from "lucide-react";
import { toast } from "sonner";
import { useAuth, can } from "@/lib/auth";

const emptyItem = () => ({ servico_id: "", descricao: "", quantidade: 1, valor_unitario: 0 });
const emptySplit = () => ({ forma_pagamento: "pix", valor: 0, parcelas: 1, primeiro_vencimento: new Date().toISOString().slice(0, 10) });
const emptyClienteData = () => ({
  nome: "", cpf: "", cep: "", endereco: "", numero: "", complemento: "",
  bairro: "", cidade: "", estado: "", pseudonimo: "", telefone: "", email: "",
});
const emptyBrief = () => ({
  subtitulo: "", paginas_orcadas: "", sinopse: "",
  orelha_frontal: "", orelha_traseira: "", ideia_capa: "",
  referencia_capa_files: [],   // [{id, filename}]
  foto_orelha_files: [],
  diagramacao_files: [],
  capa_fechada_files: [],
  miolo_files: [],
});
const emptyForm = () => ({
  cliente_id: "", vendedor_id: "",
  cliente_mode: "existing", // "existing" or "new"
  cliente_data: emptyClienteData(),
  titulo_obra: "",
  pseudonimo: "",
  custom_services: [],
  custom_service_input: "",
  orcamento_file: null, // {id, filename}
  brief: emptyBrief(),
  items: [emptyItem()],
  desconto: 0,
  data: new Date().toISOString().slice(0, 10),
  vencimento: new Date().toISOString().slice(0, 10),
  splits: [{ ...emptySplit(), valor: 0, parcelas: 1 }],
  observacoes: "",
});

export default function Sales() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const fileRef = useRef(null);
  const [uploadingOrc, setUploadingOrc] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);

  const load = async () => {
    // Todos os perfis com acesso a Vendas precisam ver TODOS os clientes para selecionar
    // (inclusive vendedor — usuário pediu lista global em "Cliente existente")
    const res = await Promise.all([
      api.get("/sales"),
      api.get("/sellers"),
      api.get("/services"),
      api.get("/clients?all=true"),
    ]);
    setItems(res[0].data); setSellers(res[1].data); setServices(res[2].data);
    setClients(res[3].data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const subtotal = form.items.reduce((acc, it) => acc + (Number(it.quantidade) || 0) * (Number(it.valor_unitario) || 0), 0);
  const total = Math.max(0, subtotal - (Number(form.desconto) || 0));

  const openNew = () => {
    setEditing(null);
    const initial = emptyForm();
    if (user.role === "vendedor" && user.seller_id) initial.vendedor_id = user.seller_id;
    initial.cliente_mode = "new"; // vendedores e novos cadastros começam em "novo cliente"
    setForm(initial);
    setOpen(true);
  };
  const openEdit = async (s) => {
    setEditing(s);
    // Reconstrói as formas de pagamento (splits) a partir das parcelas reais (receivables)
    // agrupando sequências consecutivas com a mesma forma de pagamento.
    let splits = null;
    try {
      const { data: full } = await api.get(`/sales/${s.id}`);
      const parcelas = (full.parcelas || []).slice().sort((a, b) => a.parcela - b.parcela);
      if (parcelas.length) {
        const groups = [];
        for (const p of parcelas) {
          const last = groups[groups.length - 1];
          if (last && last.forma_pagamento === (p.forma_pagamento || s.forma_pagamento)) {
            last.count += 1; last.valor += p.valor;
          } else {
            groups.push({ forma_pagamento: p.forma_pagamento || s.forma_pagamento || "pix", count: 1, valor: p.valor, primeiro_vencimento: p.vencimento });
          }
        }
        splits = groups.map(g => ({ forma_pagamento: g.forma_pagamento, valor: parseFloat(g.valor.toFixed(2)), parcelas: g.count, primeiro_vencimento: g.primeiro_vencimento }));
      }
    } catch { /* fallback below */ }
    setForm({
      cliente_id: s.cliente_id, vendedor_id: s.vendedor_id,
      cliente_mode: "existing",
      cliente_data: emptyClienteData(),
      titulo_obra: s.titulo_obra || "",
      pseudonimo: s.pseudonimo || "",
      custom_services: s.custom_services || [],
      custom_service_input: "",
      orcamento_file: s.orcamento_file_id ? { id: s.orcamento_file_id, filename: "Orçamento.pdf" } : null,
      items: s.items && s.items.length ? s.items.map(it => ({ ...it })) : [{ servico_id: s.servico_id || "", descricao: "", quantidade: 1, valor_unitario: s.valor }],
      desconto: s.desconto || 0,
      data: s.data, vencimento: s.vencimento || s.data,
      splits: splits || [{ forma_pagamento: s.forma_pagamento || "pix", valor: s.valor, parcelas: 1, primeiro_vencimento: s.vencimento || s.data }],
      observacoes: s.observacoes || "",
    });
    setOpen(true);
  };

  const [viewing, setViewing] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const openView = async (s) => {
    setViewOpen(true);
    setViewing(null);
    try {
      const { data: full } = await api.get(`/sales/${s.id}`);
      setViewing(full);
    } catch { toast.error("Erro ao carregar venda"); setViewOpen(false); }
  };

  const save = async () => {
    // Validações
    if (form.cliente_mode === "existing") {
      if (!form.cliente_id) return toast.error("Selecione um cliente ou marque 'Novo cliente'");
    } else {
      if (!form.cliente_data.nome) return toast.error("Informe o nome completo do cliente");
    }
    if (user.role !== "vendedor" && !form.vendedor_id) return toast.error("Selecione o vendedor");
    if (form.items.some(it => !it.servico_id)) return toast.error("Selecione o serviço em cada item");
    const splitsTotal = form.splits.reduce((a, s) => a + (Number(s.valor) || 0), 0);
    if (Math.abs(splitsTotal - total) > 0.01) {
      return toast.error(`Soma das formas de pagamento (${splitsTotal.toFixed(2)}) deve ser igual ao total (${total.toFixed(2)})`);
    }
    try {
      // Build parcelas from splits
      const parcelas = [];
      let parcelaNum = 1;
      for (const sp of form.splits) {
        const qtd = Math.max(1, parseInt(sp.parcelas) || 1);
        const valorParcela = (Number(sp.valor) || 0) / qtd;
        const base = new Date((sp.primeiro_vencimento || form.vencimento || form.data) + "T00:00:00");
        for (let i = 0; i < qtd; i++) {
          const d = new Date(base); d.setMonth(d.getMonth() + i);
          parcelas.push({
            numero: parcelaNum++,
            valor: parseFloat(valorParcela.toFixed(2)),
            vencimento: d.toISOString().slice(0, 10),
            forma_pagamento: sp.forma_pagamento,
          });
        }
      }
      const payload = {
        cliente_id: form.cliente_mode === "existing" ? form.cliente_id : "",
        cliente_data: form.cliente_mode === "new" ? form.cliente_data : null,
        vendedor_id: form.vendedor_id,
        items: form.items.map(it => ({ servico_id: it.servico_id, descricao: it.descricao || "", quantidade: parseFloat(it.quantidade) || 1, valor_unitario: parseFloat(it.valor_unitario) || 0 })),
        desconto: parseFloat(form.desconto) || 0,
        data: form.data, vencimento: form.vencimento || form.data,
        forma_pagamento: form.splits[0]?.forma_pagamento || "pix",
        observacoes: form.observacoes, parcelas,
        titulo_obra: form.titulo_obra || "",
        pseudonimo: form.pseudonimo || "",
        custom_services: form.custom_services || [],
        orcamento_file_id: form.orcamento_file?.id || "",
      };
      if (editing) await api.put(`/sales/${editing.id}`, payload);
      else await api.post("/sales", payload);
      toast.success(editing ? "Venda atualizada" : "Venda criada");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  // CEP auto-fill via ViaCEP
  const fetchCEP = async (cep) => {
    const clean = (cep || "").replace(/\D/g, "");
    if (clean.length !== 8) return;
    setCepLoading(true);
    try {
      const r = await fetch(`https://viacep.com.br/ws/${clean}/json/`);
      const d = await r.json();
      if (d.erro) { toast.error("CEP não encontrado"); return; }
      setForm(f => ({
        ...f,
        cliente_data: {
          ...f.cliente_data,
          cep: clean,
          endereco: d.logradouro || f.cliente_data.endereco,
          bairro: d.bairro || f.cliente_data.bairro,
          cidade: d.localidade || f.cliente_data.cidade,
          estado: d.uf || f.cliente_data.estado,
        },
      }));
    } catch { toast.error("Erro ao buscar CEP"); }
    finally { setCepLoading(false); }
  };

  const onPickOrcamento = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) { toast.error("PDF acima de 20MB"); return; }
    setUploadingOrc(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await fetch(`${API}/files/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        credentials: "include",
        body: fd,
      });
      if (!r.ok) throw new Error("upload");
      const data = await r.json();
      setForm(prev => ({ ...prev, orcamento_file: { id: data.id, filename: f.name } }));
      toast.success("Orçamento anexado");
    } catch { toast.error("Falha no upload"); }
    finally {
      setUploadingOrc(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // Generic upload for brief attachments (multi-file)
  const [briefUploading, setBriefUploading] = useState({});
  const uploadBriefFile = async (key, file) => {
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) { toast.error("Arquivo acima de 200MB"); return; }
    setBriefUploading(s => ({ ...s, [key]: true }));
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await fetch(`${API}/files/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        credentials: "include", body: fd,
      });
      if (!r.ok) throw new Error("upload");
      const d = await r.json();
      setForm(prev => ({
        ...prev,
        brief: {
          ...prev.brief,
          [key]: [...(prev.brief[key] || []), { id: d.id, filename: file.name }],
        },
      }));
      toast.success(`${file.name} anexado`);
    } catch { toast.error("Falha no upload"); }
    finally { setBriefUploading(s => ({ ...s, [key]: false })); }
  };
  const removeBriefFile = (key, idx) => {
    setForm(prev => ({
      ...prev,
      brief: {
        ...prev.brief,
        [key]: (prev.brief[key] || []).filter((_, i) => i !== idx),
      },
    }));
  };

  const addCustomService = () => {
    const v = (form.custom_service_input || "").trim();
    if (!v) return;
    if (!form.custom_services.includes(v)) {
      setForm(f => ({ ...f, custom_services: [...f.custom_services, v], custom_service_input: "" }));
    }
  };
  const rmCustomService = (s) => setForm(f => ({ ...f, custom_services: f.custom_services.filter(x => x !== s) }));

  const remove = async (s) => {
    if (!window.confirm(`Excluir venda ${s.numero}?`)) return;
    try { await api.delete(`/sales/${s.id}`); toast.success("Venda excluída"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const onSvc = (idx, sid) => {
    const s = services.find(x => x.id === sid);
    const arr = [...form.items];
    arr[idx] = { ...arr[idx], servico_id: sid, valor_unitario: s?.valor ?? arr[idx].valor_unitario, descricao: s?.nome ?? "" };
    // Auto-update first split valor to match new total if there's only one split
    const newItems = arr;
    const newSubtotal = newItems.reduce((acc, it) => acc + (Number(it.quantidade) || 0) * (Number(it.valor_unitario) || 0), 0);
    const newTotal = Math.max(0, newSubtotal - (Number(form.desconto) || 0));
    let newSplits = form.splits;
    if (form.splits.length === 1) {
      newSplits = [{ ...form.splits[0], valor: newTotal }];
    }
    setForm({ ...form, items: newItems, splits: newSplits });
  };

  const updSplit = (idx, key, val) => {
    const arr = [...form.splits];
    arr[idx] = { ...arr[idx], [key]: val };
    setForm({ ...form, splits: arr });
  };
  const addSplit = () => {
    const remaining = Math.max(0, total - form.splits.reduce((a, s) => a + (Number(s.valor) || 0), 0));
    setForm({ ...form, splits: [...form.splits, { ...emptySplit(), valor: remaining, primeiro_vencimento: form.vencimento || form.data }] });
  };
  const rmSplit = (idx) => setForm({ ...form, splits: form.splits.filter((_, i) => i !== idx) });
  const splitsTotal = form.splits.reduce((a, s) => a + (Number(s.valor) || 0), 0);

  const updItem = (idx, key, val) => {
    const arr = [...form.items];
    arr[idx] = { ...arr[idx], [key]: val };
    setForm({ ...form, items: arr });
  };

  const addItem = () => setForm({ ...form, items: [...form.items, emptyItem()] });
  const rmItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const cName = (id) => clients.find(c => c.id === id)?.nome || "—";
  const sName = (id) => sellers.find(s => s.id === id)?.nome || "—";

  return (
    <Layout title="Vendas" subtitle="Registro de vendas com múltiplos serviços">
      {can(user, "create_sales") && (
        <div className="flex justify-end mb-4">
          <Button data-testid="sale-new" onClick={openNew}><Plus className="w-4 h-4 mr-1"/>Nova Venda</Button>
        </div>
      )}
      <Card className="rounded-sm overflow-hidden">
        <Table>
          <TableHeader className="bg-secondary/60"><TableRow>
            <TableHead>Nº</TableHead><TableHead>Data</TableHead><TableHead>Cliente</TableHead><TableHead>Vendedor</TableHead>
            <TableHead>Itens</TableHead><TableHead>Desc.</TableHead><TableHead>Total</TableHead><TableHead>Forma</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow></TableHeader>
          <TableBody>{items.map(s => (
            <TableRow key={s.id} data-testid={`sale-row-${s.id}`}>
              <TableCell className="font-mono text-xs">{s.numero}</TableCell>
              <TableCell>{fmtDate(s.data)}</TableCell>
              <TableCell>{cName(s.cliente_id)}</TableCell>
              <TableCell>{sName(s.vendedor_id)}</TableCell>
              <TableCell className="text-xs">{(s.items || []).length || 1} item(ns)</TableCell>
              <TableCell>{s.desconto ? fmtBRL(s.desconto) : "—"}</TableCell>
              <TableCell className="font-semibold">{fmtBRL(s.valor)}</TableCell>
              <TableCell className="capitalize">{s.forma_pagamento}</TableCell>
              <TableCell className="text-right">
                <Button data-testid={`sale-view-${s.id}`} variant="ghost" size="sm" onClick={() => openView(s)}><Eye className="w-4 h-4"/></Button>
                {can(user, "manage_sales") && <>
                  <Button data-testid={`sale-edit-${s.id}`} variant="ghost" size="sm" onClick={() => openEdit(s)}><Pencil className="w-4 h-4"/></Button>
                  <Button data-testid={`sale-delete-${s.id}`} variant="ghost" size="sm" onClick={() => remove(s)}><Trash2 className="w-4 h-4"/></Button>
                </>}
              </TableCell>
            </TableRow>
          ))}{items.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-6">Sem vendas</TableCell></TableRow>}</TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Editar Venda ${editing.numero}` : "Nova Venda Editorial"}</DialogTitle>
            <DialogDescription>
              Preencha os dados do cliente, da obra, serviços contratados, formas de pagamento e anexe o orçamento em PDF.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5">

            {/* Cliente */}
            <section className="space-y-3 p-3 border border-border rounded-sm bg-muted/20">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold uppercase tracking-wider text-[hsl(var(--primary))]">Cliente</Label>
                <div className="flex gap-1 text-[10px]">
                  <Button
                    type="button" size="sm"
                    variant={form.cliente_mode === "existing" ? "default" : "outline"}
                    onClick={() => setForm({ ...form, cliente_mode: "existing" })}
                    data-testid="sale-cliente-existing"
                  >Existente</Button>
                  <Button
                    type="button" size="sm"
                    variant={form.cliente_mode === "new" ? "default" : "outline"}
                    onClick={() => setForm({ ...form, cliente_mode: "new" })}
                    data-testid="sale-cliente-new"
                  >Novo cliente</Button>
                </div>
              </div>

              {form.cliente_mode === "existing" ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Cliente cadastrado</Label>
                    <SearchableSelect
                      value={form.cliente_id}
                      onValueChange={(v) => setForm({...form, cliente_id: v})}
                      options={clients.map(c => ({ value: c.id, label: c.nome, hint: c.cpf || c.email || "" }))}
                      placeholder="Selecionar..."
                      searchPlaceholder="Buscar por nome, CPF ou e-mail..."
                      testid="sale-cliente"
                      className="mt-1"
                    />
                  </div>
                  {user.role !== "vendedor" && (
                    <div>
                      <Label className="text-xs">Vendedor</Label>
                      <SearchableSelect
                        value={form.vendedor_id}
                        onValueChange={(v) => setForm({...form, vendedor_id: v})}
                        options={sellers.map(s => ({ value: s.id, label: s.nome }))}
                        placeholder="Selecionar..."
                        searchPlaceholder="Buscar vendedor..."
                        testid="sale-vendedor"
                        className="mt-1"
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-7">
                    <Label className="text-xs">Nome completo *</Label>
                    <Input data-testid="sale-cd-nome" value={form.cliente_data.nome} onChange={(e) => setForm({...form, cliente_data: { ...form.cliente_data, nome: e.target.value }})} className="mt-1" />
                  </div>
                  <div className="col-span-5">
                    <Label className="text-xs">CPF</Label>
                    <Input data-testid="sale-cd-cpf" value={form.cliente_data.cpf} onChange={(e) => setForm({...form, cliente_data: { ...form.cliente_data, cpf: e.target.value }})} className="mt-1" />
                  </div>
                  <div className="col-span-4">
                    <Label className="text-xs">CEP</Label>
                    <div className="flex gap-1 mt-1">
                      <Input
                        data-testid="sale-cd-cep"
                        value={form.cliente_data.cep}
                        onChange={(e) => setForm({...form, cliente_data: { ...form.cliente_data, cep: e.target.value }})}
                        onBlur={() => fetchCEP(form.cliente_data.cep)}
                        placeholder="00000-000"
                      />
                      {cepLoading && <Loader2 className="w-4 h-4 animate-spin mt-2.5" />}
                    </div>
                  </div>
                  <div className="col-span-6">
                    <Label className="text-xs">Endereço</Label>
                    <Input data-testid="sale-cd-endereco" value={form.cliente_data.endereco} onChange={(e) => setForm({...form, cliente_data: { ...form.cliente_data, endereco: e.target.value }})} className="mt-1" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Nº</Label>
                    <Input data-testid="sale-cd-numero" value={form.cliente_data.numero} onChange={(e) => setForm({...form, cliente_data: { ...form.cliente_data, numero: e.target.value }})} className="mt-1" />
                  </div>
                  <div className="col-span-4">
                    <Label className="text-xs">Bairro</Label>
                    <Input value={form.cliente_data.bairro} onChange={(e) => setForm({...form, cliente_data: { ...form.cliente_data, bairro: e.target.value }})} className="mt-1" />
                  </div>
                  <div className="col-span-4">
                    <Label className="text-xs">Cidade</Label>
                    <Input value={form.cliente_data.cidade} onChange={(e) => setForm({...form, cliente_data: { ...form.cliente_data, cidade: e.target.value }})} className="mt-1" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">UF</Label>
                    <Input value={form.cliente_data.estado} onChange={(e) => setForm({...form, cliente_data: { ...form.cliente_data, estado: e.target.value }})} className="mt-1" />
                  </div>
                  <div className="col-span-6">
                    <Label className="text-xs">Pseudônimo</Label>
                    <Input data-testid="sale-cd-pseudonimo" value={form.cliente_data.pseudonimo} onChange={(e) => setForm({...form, cliente_data: { ...form.cliente_data, pseudonimo: e.target.value }, pseudonimo: e.target.value })} className="mt-1" placeholder="Nome literário (se houver)" />
                  </div>
                  <div className="col-span-6">
                    <Label className="text-xs">Telefone</Label>
                    <Input value={form.cliente_data.telefone} onChange={(e) => setForm({...form, cliente_data: { ...form.cliente_data, telefone: e.target.value }})} className="mt-1" />
                  </div>
                  <div className="col-span-6">
                    <Label className="text-xs">E-mail</Label>
                    <Input data-testid="sale-cd-email" type="email" value={form.cliente_data.email} onChange={(e) => setForm({...form, cliente_data: { ...form.cliente_data, email: e.target.value }})} className="mt-1" placeholder="autor@exemplo.com" />
                  </div>
                  {user.role !== "vendedor" && (
                    <div className="col-span-6">
                      <Label className="text-xs">Vendedor *</Label>
                      <SearchableSelect
                        value={form.vendedor_id}
                        onValueChange={(v) => setForm({...form, vendedor_id: v})}
                        options={sellers.map(s => ({ value: s.id, label: s.nome }))}
                        placeholder="Selecionar..."
                        searchPlaceholder="Buscar vendedor..."
                        testid="sale-vendedor-new"
                        className="mt-1"
                      />
                    </div>
                  )}
                  {user.role === "vendedor" && (
                    <div className="col-span-12 text-[10px] text-muted-foreground italic">Sua venda será atribuída automaticamente a você.</div>
                  )}
                </div>
              )}
            </section>

            {/* Obra */}
            <section className="space-y-3 p-3 border border-border rounded-sm bg-muted/20">
              <Label className="text-xs font-bold uppercase tracking-wider text-[hsl(var(--primary))] flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5"/>Obra</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className={form.cliente_mode === "new" ? "col-span-2" : ""}>
                  <Label className="text-xs">Título da obra</Label>
                  <Input data-testid="sale-titulo-obra" value={form.titulo_obra} onChange={(e) => setForm({...form, titulo_obra: e.target.value})} className="mt-1" />
                </div>
                {form.cliente_mode === "existing" && (
                  <div>
                    <Label className="text-xs">Pseudônimo (na obra)</Label>
                    <Input data-testid="sale-pseudonimo" value={form.pseudonimo} onChange={(e) => setForm({...form, pseudonimo: e.target.value})} className="mt-1" />
                  </div>
                )}
              </div>
            </section>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold uppercase tracking-wider">Serviços contratados</Label>
                <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="sale-add-item"><Plus className="w-3 h-3 mr-1"/>Adicionar serviço</Button>
              </div>
              <div className="space-y-2">
                {form.items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end p-3 border border-border rounded-sm bg-muted/30">
                    <div className="col-span-5">
                      <Label className="text-[10px] text-muted-foreground">Serviço</Label>
                      <SearchableSelect
                        value={it.servico_id}
                        onValueChange={(v) => onSvc(idx, v)}
                        options={services.map(s => ({ value: s.id, label: s.nome, hint: s.preco_padrao ? `R$ ${Number(s.preco_padrao).toFixed(2)}` : "" }))}
                        placeholder="Selecionar..."
                        searchPlaceholder="Buscar serviço..."
                        testid={`sale-item-${idx}-servico`}
                        className="mt-1"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px] text-muted-foreground">Qtd</Label>
                      <Input data-testid={`sale-item-${idx}-qtd`} type="number" min="0" step="0.01" value={it.quantidade} onChange={(e) => updItem(idx, "quantidade", e.target.value)} className="mt-1"/>
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px] text-muted-foreground">Valor unit.</Label>
                      <Input data-testid={`sale-item-${idx}-unit`} type="number" min="0" step="0.01" value={it.valor_unitario} onChange={(e) => updItem(idx, "valor_unitario", e.target.value)} className="mt-1"/>
                    </div>
                    <div className="col-span-1 text-right">
                      <div className="text-[10px] text-muted-foreground">Total</div>
                      <div className="text-sm font-semibold tabular-nums pt-2">{fmtBRL((Number(it.quantidade) || 0) * (Number(it.valor_unitario) || 0))}</div>
                    </div>
                    <div className="col-span-1 flex justify-end">
                      {form.items.length > 1 && (
                        <Button type="button" size="sm" variant="ghost" onClick={() => rmItem(idx)} data-testid={`sale-item-${idx}-rm`}><X className="w-4 h-4"/></Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {/* Serviços manuais (não cadastrados no sistema) */}
              <div className="mt-3 p-3 border border-dashed border-border rounded-sm bg-muted/10">
                <Label className="text-xs font-semibold">Outros serviços (manual)</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    data-testid="sale-custom-input"
                    placeholder="Ex.: revisão extra, ilustração customizada…"
                    value={form.custom_service_input}
                    onChange={(e) => setForm({...form, custom_service_input: e.target.value})}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomService(); } }}
                  />
                  <Button type="button" size="sm" variant="outline" onClick={addCustomService} data-testid="sale-custom-add">Adicionar</Button>
                </div>
                {(form.custom_services || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {form.custom_services.map(s => (
                      <span key={s} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-secondary border border-border rounded-sm">
                        {s}
                        <button type="button" onClick={() => rmCustomService(s)} className="hover:text-red-600">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-muted-foreground mt-1">Estes serviços aparecem como complemento — sem valor automático. Adicione ao item correspondente acima se quiser cobrar.</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 items-end">
              <div>
                <Label className="text-xs font-semibold">Desconto (R$)</Label>
                <Input data-testid="sale-desconto" type="number" min="0" step="0.01" value={form.desconto} onChange={(e) => {
                  const newDesc = e.target.value;
                  const newTotal = Math.max(0, subtotal - (Number(newDesc) || 0));
                  let newSplits = form.splits;
                  if (form.splits.length === 1) newSplits = [{ ...form.splits[0], valor: newTotal }];
                  setForm({ ...form, desconto: newDesc, splits: newSplits });
                }} className="mt-1"/>
              </div>
              <div className="col-span-2 grid grid-cols-3 gap-3 text-right p-3 border border-border rounded-sm bg-muted/40">
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Subtotal</div><div className="kpi-num text-lg">{fmtBRL(subtotal)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Desconto</div><div className="kpi-num text-lg text-red-700">-{fmtBRL(Number(form.desconto) || 0)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</div><div className="kpi-num text-lg text-primary">{fmtBRL(total)}</div></div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold">Data da venda</Label><Input data-testid="sale-data" type="date" value={form.data} onChange={(e) => setForm({...form, data: e.target.value})} className="mt-1"/></div>
              <div><Label className="text-xs font-semibold">1º Vencimento padrão</Label><Input data-testid="sale-vencimento" type="date" value={form.vencimento} onChange={(e) => setForm({...form, vencimento: e.target.value})} className="mt-1"/></div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold uppercase tracking-wider">Formas de Pagamento</Label>
                <Button type="button" size="sm" variant="outline" onClick={addSplit} data-testid="sale-add-split"><Plus className="w-3 h-3 mr-1"/>Adicionar forma</Button>
              </div>
              <p className="text-[10px] text-muted-foreground mb-2">Divida o total em múltiplas formas (ex.: R$2.000 PIX + R$3.000 Cartão em 3x). A soma deve ser igual ao total da venda.</p>
              <div className="space-y-2">
                {form.splits.map((sp, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end p-3 border border-border rounded-sm bg-muted/30">
                    <div className="col-span-3">
                      <Label className="text-[10px] text-muted-foreground">Forma</Label>
                      <Select value={sp.forma_pagamento} onValueChange={(v) => updSplit(idx, "forma_pagamento", v)}>
                        <SelectTrigger data-testid={`sale-split-${idx}-forma`} className="mt-1"><SelectValue/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pix">PIX</SelectItem><SelectItem value="boleto">Boleto</SelectItem>
                          <SelectItem value="cartao">Cartão</SelectItem><SelectItem value="transferencia">Transferência</SelectItem>
                          <SelectItem value="dinheiro">Dinheiro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px] text-muted-foreground">Valor (R$)</Label>
                      <Input data-testid={`sale-split-${idx}-valor`} type="number" min="0" step="0.01" value={sp.valor} onChange={(e) => updSplit(idx, "valor", e.target.value)} className="mt-1"/>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px] text-muted-foreground">Parcelas</Label>
                      <Input data-testid={`sale-split-${idx}-parc`} type="number" min="1" max="36" value={sp.parcelas} onChange={(e) => updSplit(idx, "parcelas", e.target.value)} className="mt-1"/>
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px] text-muted-foreground">1º vencimento</Label>
                      <Input type="date" value={sp.primeiro_vencimento || form.vencimento} onChange={(e) => updSplit(idx, "primeiro_vencimento", e.target.value)} className="mt-1"/>
                    </div>
                    <div className="col-span-1 flex justify-end">
                      {form.splits.length > 1 && (
                        <Button type="button" size="sm" variant="ghost" onClick={() => rmSplit(idx)} data-testid={`sale-split-${idx}-rm`}><X className="w-4 h-4"/></Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className={`mt-2 text-xs text-right ${Math.abs(splitsTotal - total) > 0.01 ? "text-red-700 font-semibold" : "text-emerald-700"}`}>
                Soma das formas: {fmtBRL(splitsTotal)} {Math.abs(splitsTotal - total) > 0.01 ? `≠ Total ${fmtBRL(total)}` : "✓"}
              </div>
            </div>
            {/* Anexo PDF do orçamento */}
            <div className="p-3 border border-border rounded-sm bg-muted/20">
              <Label className="text-xs font-bold uppercase tracking-wider text-[hsl(var(--primary))] flex items-center gap-1.5">
                <Paperclip className="w-3.5 h-3.5"/>Orçamento (PDF)
              </Label>
              {form.orcamento_file ? (
                <div className="mt-2 flex items-center gap-2 p-2 bg-secondary border border-border rounded-sm text-xs">
                  <FileText className="w-4 h-4 text-[hsl(var(--primary))]" />
                  <span className="flex-1 truncate">{form.orcamento_file.filename}</span>
                  <a
                    href={`${API}/files/${form.orcamento_file.id}/download`}
                    target="_blank" rel="noreferrer"
                    className="text-[hsl(var(--primary))] underline text-[10px]"
                  >abrir</a>
                  <button onClick={() => setForm(f => ({...f, orcamento_file: null}))} className="hover:text-red-600" data-testid="sale-orc-remove">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="mt-2">
                  <input ref={fileRef} type="file" accept="application/pdf" hidden onChange={onPickOrcamento} data-testid="sale-orc-input" />
                  <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploadingOrc} data-testid="sale-orc-upload">
                    {uploadingOrc ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/> : <Paperclip className="w-3.5 h-3.5 mr-1"/>}
                    Anexar orçamento em PDF
                  </Button>
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs font-semibold">Observações</Label>
              <Input value={form.observacoes} onChange={(e) => setForm({...form, observacoes: e.target.value})} className="mt-1"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="sale-save">{editing ? "Salvar Alterações" : "Criar Venda"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewing ? `Venda ${viewing.numero}` : "Carregando..."}</DialogTitle>
            <DialogDescription>Detalhes completos da venda, incluindo todas as parcelas geradas.</DialogDescription>
          </DialogHeader>
          {!viewing ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground"/></div>
          ) : (
            <div className="space-y-4" data-testid="sale-view-content">
              <div className="grid grid-cols-2 gap-3 p-3 border border-border rounded-sm bg-muted/20">
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Cliente</div><div className="text-sm font-medium">{cName(viewing.cliente_id)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Vendedor</div><div className="text-sm font-medium">{sName(viewing.vendedor_id)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Data da venda</div><div className="text-sm">{fmtDate(viewing.data)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Título da obra</div><div className="text-sm">{viewing.titulo_obra || "—"}</div></div>
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Itens</Label>
                <Table className="mt-1">
                  <TableHeader className="bg-secondary/60"><TableRow><TableHead>Serviço</TableHead><TableHead>Qtd</TableHead><TableHead>Valor unit.</TableHead><TableHead>Total</TableHead></TableRow></TableHeader>
                  <TableBody>{(viewing.items || []).map((it, i) => (
                    <TableRow key={i}>
                      <TableCell>{services.find(s => s.id === it.servico_id)?.nome || it.descricao || "—"}</TableCell>
                      <TableCell>{it.quantidade}</TableCell>
                      <TableCell>{fmtBRL(it.valor_unitario)}</TableCell>
                      <TableCell className="font-semibold">{fmtBRL((it.quantidade || 0) * (it.valor_unitario || 0))}</TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              </div>

              <div className="grid grid-cols-3 gap-3 p-3 border border-border rounded-sm bg-muted/40 text-right">
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Subtotal</div><div className="kpi-num text-lg">{fmtBRL(viewing.subtotal)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Desconto</div><div className="kpi-num text-lg text-red-700">-{fmtBRL(viewing.desconto)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</div><div className="kpi-num text-lg text-primary">{fmtBRL(viewing.valor)}</div></div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs font-semibold uppercase tracking-wider">Parcelas ({(viewing.parcelas || []).length})</Label>
                </div>
                <Table>
                  <TableHeader className="bg-secondary/60"><TableRow><TableHead>#</TableHead><TableHead>Vencimento</TableHead><TableHead>Valor</TableHead><TableHead>Recebido</TableHead><TableHead>Forma</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                  <TableBody data-testid="sale-view-parcelas">{(viewing.parcelas || []).map(p => (
                    <TableRow key={p.id}>
                      <TableCell>#{p.parcela}</TableCell>
                      <TableCell>{fmtDate(p.vencimento)}</TableCell>
                      <TableCell>{fmtBRL(p.valor)}</TableCell>
                      <TableCell>{fmtBRL(p.valor_recebido)}</TableCell>
                      <TableCell className="capitalize text-xs">{p.forma_pagamento || "—"}</TableCell>
                      <TableCell className="capitalize text-xs">{p.status}</TableCell>
                    </TableRow>
                  ))}{(!viewing.parcelas || viewing.parcelas.length === 0) && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-4">Sem parcelas</TableCell></TableRow>}</TableBody>
                </Table>
              </div>

              {viewing.observacoes && (
                <div><Label className="text-xs font-semibold">Observações</Label><p className="text-sm mt-1">{viewing.observacoes}</p></div>
              )}
              {viewing.orcamento_file_id && (
                <a href={`${API}/files/${viewing.orcamento_file_id}/download`} target="_blank" rel="noreferrer" className="text-xs text-[hsl(var(--primary))] underline inline-flex items-center gap-1">
                  <FileText className="w-3.5 h-3.5"/>Baixar orçamento em PDF
                </a>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setViewOpen(false)}>Fechar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function BriefUploadMulti({ label, testid, files, uploading, onPick, onRemove }) {
  const ref = useRef(null);
  const list = files || [];
  return (
    <div className="p-2 border border-border rounded-sm bg-card">
      <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="mt-1 space-y-1" data-testid={`${testid}-list`}>
        {list.map((f, i) => (
          <div key={i} className="flex items-center gap-1 text-[11px] bg-secondary/40 px-1.5 py-1 rounded-sm">
            <FileText className="w-3 h-3 text-[hsl(var(--primary))] shrink-0" />
            <span className="truncate flex-1" title={f.filename}>{f.filename}</span>
            <button onClick={() => onRemove(i)} className="text-red-600 hover:text-red-700" data-testid={`${testid}-remove-${i}`}>
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        {list.length === 0 && (
          <div className="text-[10px] text-muted-foreground italic">Nenhum arquivo anexado.</div>
        )}
      </div>
      <input ref={ref} type="file" hidden onChange={(e) => { onPick(e.target.files?.[0]); if (ref.current) ref.current.value = ""; }} data-testid={`${testid}-input`} />
      <Button size="sm" variant="outline" className="mt-2 w-full text-[10px] h-7" onClick={() => ref.current?.click()} disabled={uploading} data-testid={testid}>
        {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin"/> : <Paperclip className="w-3 h-3 mr-1" />}
        Anexar arquivo
      </Button>
    </div>
  );
}
